// <select> de Categoria (entidade) + botão "Gerenciar" que abre um modal para
// criar, renomear e inativar/reativar Categorias. Mesmo padrão do
// `_lista-select.ts`, mas sobre a entidade Categoria (ADR-0006: sem tela própria).

import type { Store } from "../store.ts";
import { clear, el, openModal, botao } from "../ui.ts";
import { criarAcaoComando } from "./_acao.ts";

export interface CategoriaSelect {
  element: HTMLElement;
  value(): string;
}

/** `valorAtual` = id da Categoria escolhida (ou ""). */
export function categoriaSelect(
  store: Store,
  valorAtual: string,
): CategoriaSelect {
  const select = el("select", {}) as HTMLSelectElement;

  const preencher = (): void => {
    const escolhido = select.value || valorAtual;
    clear(select);
    select.append(el("option", { value: "", "data-vazio": "" }, "—"));
    const ativas = [...store.get().categorias]
      .filter((c) => c.ativo)
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
    for (const c of ativas) select.append(el("option", { value: c.id }, c.nome));
    select.value = ativas.some((c) => c.id === escolhido) ? escolhido : "";
  };
  preencher();

  const gerir = botao(
    "secundario",
    { title: "Gerenciar categorias" },
    "Gerenciar",
  );
  gerir.addEventListener("click", () => abrirGerenciarCategorias(store, preencher));

  return {
    element: el("div", { class: "lista-select" }, select, gerir),
    value: () => select.value,
  };
}

/** Modal de manutenção das Categorias. `aoFechar` roda depois de cada gravação. */
export function abrirGerenciarCategorias(
  store: Store,
  aoFechar?: () => void,
): void {
  openModal("Gerenciar Categorias", () => {
    const raiz = el("div", { class: "form-modal" });
    let novoNome: HTMLInputElement;
    let editandoId: string | null = null;

    const render = (): void => {
      clear(raiz);
      const cats = [...store.get().categorias].sort((a, b) =>
        a.nome.localeCompare(b.nome, "pt-BR"),
      );
      const lista = el("ul", { class: "lista-editavel" });
      if (cats.length === 0) {
        lista.append(el("li", { class: "muted" }, "Nenhuma categoria."));
      }
      for (const c of cats) {
        if (editandoId === c.id) {
          const inp = el("input", { type: "text" }) as HTMLInputElement;
          inp.value = c.nome;
          const erro = el("span", { class: "hist-erro" });
          const ok = botao({ variante: "secundario", tamanho: "compacto" }, {}, "Salvar");
          const cancelar = botao(
            { variante: "secundario", tamanho: "compacto" },
            {},
            "Cancelar",
          );
          ok.addEventListener("click", () => {
            criarAcaoComando(store, erro)(
              {
                agregado: "categoria",
                cmd: { tipo: "editar", id: c.id, nome: inp.value },
              },
              "Categoria salva.",
              () => {
                editandoId = null;
                render();
                aoFechar?.();
              },
            );
          });
          cancelar.addEventListener("click", () => {
            editandoId = null;
            render();
          });
          lista.append(
            el("li", {}, el("div", { class: "hist-edit" }, inp, ok, cancelar, erro)),
          );
          continue;
        }
        const renomear = botao({ variante: "secundario", tamanho: "compacto" }, {}, "Editar");
        renomear.addEventListener("click", () => {
          editandoId = c.id;
          render();
        });
        const alternar = botao(
          { variante: "secundario", tamanho: "compacto" },
          {},
          c.ativo ? "Inativar" : "Reativar",
        );
        alternar.addEventListener("click", () => {
          void store
            .executar(
              {
                agregado: "categoria",
                cmd: { tipo: "definirAtivo", id: c.id, ativo: !c.ativo },
              },
              c.ativo ? "Categoria inativada." : "Categoria reativada.",
            )
            .then(
              () => {
                render();
                aoFechar?.();
              },
              // falhou: o Store já mostrou o toast; só redesenha pra o rótulo
              // do botão não ficar defasado
              () => render(),
            );
        });
        lista.append(
          el(
            "li",
            c.ativo ? {} : { class: "inativo" },
            el("span", {}, c.nome, c.ativo ? "" : " (inativa)"),
            el("span", { class: "hist-acao" }, renomear, alternar),
          ),
        );
      }

      novoNome = el("input", {
        type: "text",
        placeholder: "Nova categoria",
      }) as HTMLInputElement;
      const incluir = botao("secundario", {}, "Incluir");
      const erroNovo = el("p", { class: "form-erro", role: "alert" });
      const addUma = (): void => {
        criarAcaoComando(store, erroNovo)(
          { agregado: "categoria", cmd: { tipo: "criar", nome: novoNome.value } },
          "Categoria criada.",
          () => {
            render();
            aoFechar?.();
            novoNome.focus();
          },
        );
      };
      incluir.addEventListener("click", addUma);
      novoNome.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          addUma();
        }
      });

      raiz.append(lista, el("div", { class: "lista-add" }, novoNome, incluir), erroNovo);
    };

    render();
    return raiz;
  });
}
